import { createAccount } from '@/services/AccountsService';
import * as Sentry from '@sentry/react';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Input from '@superfunnel/ui-core/v2/Input';
import { extractDomainName, generateSlug } from '@superfunnel/utils';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CreateWorkspaceModal({ open, onClose }: Props) {
  const navigate = useNavigate();

  const { show } = useContext(SnackbarContext);
  const { refetchAccounts } = useContext(UserContext);

  const [name, setName] = useState<string>();
  const [slug, setSlug] = useState<string>();
  const [websiteUrl, setWebsiteUrl] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error>();
  const [nameTouched, setNameTouched] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (open) return;

    setName(undefined);
    setSlug(undefined);
    setWebsiteUrl(undefined);
    setCreating(false);
    setNameTouched(false);
    setSlugTouched(false);
  }, [open]);

  const handleCreateAccount = useCallback(async () => {
    if (creating) return;

    try {
      setCreating(true);

      const res = await createAccount({
        websiteUrl: websiteUrl || '',
        name: name || null,
        slug: slug || null,
        skipOnboarding: true,
      });

      await refetchAccounts();

      navigate(`/workspace/${res.slug}`);
    } catch (e: any) {
      show('Failed to create workspace. Please try again.', 'warn');
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setCreating(false);
  }, [creating, websiteUrl, name, slug, refetchAccounts, navigate, show]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: creating,
        onClick: () => onClose(),
      },
      {
        title: 'Create',
        type: 'primary',
        loading: creating,
        onClick: () => handleCreateAccount(),
      },
    ],
    [creating, onClose, handleCreateAccount],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      title="Create a new workspace"
    >
      <div className="mt-0 flex flex-col gap-4">
        <Input
          title="Your site"
          type="url"
          placeholder="www.yourcompany.com"
          value={websiteUrl}
          readOnly={creating}
          onChange={(e) => {
            const newUrl = e.target.value;
            setWebsiteUrl(newUrl);

            // Auto-populate name if not touched
            if (!nameTouched) {
              const extractedName = extractDomainName(newUrl);
              setName(extractedName);
              // Auto-generate slug from extracted name if slug not touched
              if (!slugTouched) {
                setSlug(generateSlug(extractedName));
              }
            } else if (!slugTouched && name) {
              // If name exists and slug not touched, regenerate slug
              setSlug(generateSlug(name));
            }
          }}
        />
        <Input
          title="Company name"
          placeholder="Company Inc."
          value={name}
          readOnly={creating}
          onChange={(e) => {
            const newName = e.target.value;
            setNameTouched(true);
            setName(newName);

            // Auto-generate slug if not touched
            if (!slugTouched && newName) {
              setSlug(generateSlug(newName));
            }
          }}
        />
        <Input
          title="Slug"
          placeholder="yourcompany"
          value={slug}
          readOnly={creating}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
        />
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
